import React, { useState, useContext } from 'react';
import authService from '../services/authService';
import axiosInstance from '../services/axiosInstance';
import invitationService from '../services/invitationService';
import languageService from '../services/languageService';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import '../styles/LoginForm.css';
import { useTranslation } from 'react-i18next';
import { FaGlobe, FaEye, FaEyeSlash } from 'react-icons/fa';
import { CiLock, CiMail } from "react-icons/ci";
import { toast } from 'sonner';
import { trackEvent } from '../utils/analytics';
import SEO from './SEO';
import InviteSuccess from './InviteSuccess';

const LoginForm = () => {
    const { login } = useContext(AuthContext);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    
    const [inviteSuccess, setInviteSuccess] = useState(false);

    const { t, i18n } = useTranslation();

    const languages = {
        'en': { name: 'English', flag: '/flags/en.png' },
        'cs': { name: 'Čeština', flag: '/flags/cs.png' }
    };

    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!googleClientId) {
        console.error('Google Client ID not configured');
    }

    const getErrorMessage = (error) => {
        if (!error.response) {
            if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
                return t('login.errors.serverDown', 'Server is currently unavailable. Please try again later.');
            }
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                return t('login.errors.timeout', 'Connection timeout. Please check your internet connection and try again.');
            }
            return t('login.errors.networkError', 'Unable to connect to server. Please check your internet connection.');
        }

        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
            case 401:
                return t('login.errors.invalidCredentials', 'Invalid email or password.');
            case 403:
                if (typeof data === 'string' && data.includes('Maximum number of users')) {
                    return t('login.errors.userLimitReached', data);
                }
                return t('login.errors.accountLocked', 'Account is temporarily locked. Please try again later.');
            case 429:
                return t('login.errors.tooManyAttempts', 'Too many login attempts. Please wait before trying again.');
            case 500:
                return t('login.errors.serverError', 'Server error occurred. Please try again later.');
            case 502:
            case 503:
            case 504:
                return t('login.errors.serverUnavailable', 'Server is temporarily unavailable. Please try again later.');
            default:
                if (typeof data === 'string') return data;
                if (data && data.message) return data.message;
                return t('login.errors.general', 'An error occurred during login. Please try again.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // 1. Získáme token/session od serveru
            // Poznámka: Záměrně zde ještě nevoláme `await login()`, abychom nespustili
            // přesměrování v App.js a zůstali na stránce pro animaci.
            await authService.loginUser({ username, password });
            
            const pendingInvite = localStorage.getItem('pendingInvite');
            if (pendingInvite) {
                try {
                    const { leagueId, inviteToken } = JSON.parse(pendingInvite);
                    
                    // 2. Akceptujeme pozvánku (session cookie už máme z kroku 1)
                    await invitationService.acceptInvite(leagueId, inviteToken);
                    trackEvent('Invite Accepted', { source: 'pending_login' });
                    
                    localStorage.removeItem('pendingInvite');
                    
                    // 3. Zobrazíme animaci
                    setInviteSuccess(true);
                    
                    // 4. Až po 2 sekundách aktualizujeme globální stav a přesměrujeme
                    setTimeout(async () => {
                        await login(); // Teprve teď řekneme Reactu "jsem přihlášen"
                        trackEvent('Login', { method: 'email' });
                        navigate(`/league/${leagueId}`); // A hned jdeme do ligy (přebijeme default redirect)
                    }, 2000);
                    
                } catch (err) {
                    console.error("Auto-accept failed", err);
                    // Fallback: Pokud pozvánka selže, přihlásíme uživatele a pošleme ho na invite page
                    const { leagueId, inviteToken } = JSON.parse(pendingInvite);
                    localStorage.removeItem('pendingInvite');
                    
                    await login();
                    trackEvent('Login', { method: 'email' });
                    navigate(`/invite/league/${leagueId}/${inviteToken}`);
                }
            } else {
                // Standardní flow bez pozvánky
                await login();
                trackEvent('Login', { method: 'email' });
                navigate('/dashboard');
            }
        } catch (error) {
            trackEvent('Login Failed', { method: 'email', error: 'credentials' });
            const errorMessage = getErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            // Loading vypneme jen pokud NEJSME v úspěšném stavu (aby formulář neproblikl)
            if (!inviteSuccess) {
                setLoading(false);
            }
        }
    };

    const handleGoogleLoginSuccess = async (credentialResponse) => {
        setLoading(true);

        try {
            await axiosInstance.get('/auth/csrf');
            // 1. Login na serveru
            await authService.loginWithGoogle(credentialResponse.credential);
            
            // Opět: nevoláme `await login()` hned, pokud máme pozvánku

            const pendingInvite = localStorage.getItem('pendingInvite');
            if (pendingInvite) {
                try {
                    const { leagueId, inviteToken } = JSON.parse(pendingInvite);
                    const message = await invitationService.acceptInvite(leagueId, inviteToken);
                    trackEvent('Invite Accepted', { source: 'pending_login' });
                    toast.success(message);
                    
                    localStorage.removeItem('pendingInvite');
                    
                    setInviteSuccess(true);
                    
                    setTimeout(async () => {
                        await login();
                        trackEvent('Login', { method: 'google' });
                        navigate(`/league/${leagueId}`);
                    }, 2000);
                    
                } catch (err) {
                    const { leagueId, inviteToken } = JSON.parse(pendingInvite);
                    localStorage.removeItem('pendingInvite');
                    
                    await login();
                    navigate(`/invite/league/${leagueId}/${inviteToken}`);
                }
            } else {
                await login();
                trackEvent('Login', { method: 'google' });
                navigate('/dashboard');
            }
        } catch (error) {
            console.error('Google login error:', error);
            trackEvent('Login Failed', { method: 'google' });
            const errorMessage = getErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            if (!inviteSuccess) {
                setLoading(false);
            }
        }
    };

    const handleGoogleLoginError = () => {
        toast.error(t('login.errors.googleError', 'Error logging in with Google. Please try again or use email login.'));
        setLoading(false);
    };

    const handleLanguageChange = (lang) => {
        languageService.changeLanguageAndPersist(i18n, lang);
        setLanguageDropdownOpen(false);
    };

    const toggleLanguageDropdown = () => {
        setLanguageDropdownOpen(!languageDropdownOpen);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="login-container">
            <SEO 
                title={t('signIn', { defaultValue: "Přihlášení" })}
            />
            <div className="login-language-selector">
                <button
                    onClick={toggleLanguageDropdown}
                    className="language-toggle-btn"
                    aria-label="Change language"
                >
                    <FaGlobe />
                </button>
                {languageDropdownOpen && (
                    <div className="language-dropdown">
                        {Object.entries(languages).map(([code, { name, flag }]) => (
                            <div
                                key={code}
                                onClick={() => handleLanguageChange(code)}
                                className="language-option"
                            >
                                <img
                                    src={flag}
                                    alt={`${name} flag`}
                                    className="language-flag"
                                />
                                {name}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="login-header">
                <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <h2>Scoreo</h2>
                </Link>
                {/* Texty zobrazíme jen pokud neběží success animace */}
                {!inviteSuccess && (
                    <>
                        <h3>{t('login.welcomeBack')}</h3>
                        <p>{t('login.info')}</p>
                    </>
                )}
            </div>

            <div className="login-card-wrapper">
                <div className="login-card">
                    {/* Podmíněné renderování: Animace vs Formulář */}
                    {inviteSuccess ? (
                        <InviteSuccess 
                            subtitle={t('invite.joining', 'Připojování...')}
                        />
                    ) : (
                        <>
                            <form onSubmit={handleSubmit}>
                                <div className="input-group">
                                    <label htmlFor="username">Email *</label>
                                    <div className="input-wrapper">
                                        <CiMail className="input-icon" />
                                        <input
                                            type="email"
                                            id="username"
                                            placeholder="jan.novak@gmail.com"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="input-group">
                                    <label htmlFor="password">{t('password')} *</label>
                                    <div className="forgot-password">
                                        <a href="/forgot-password">{t('forgotPassword.link')}</a>
                                    </div>
                                    <div className="input-wrapper">
                                        <CiLock className="input-icon" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            placeholder="************"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                        {showPassword ? (
                                            <FaEyeSlash className="password-toggle-icon" onClick={togglePasswordVisibility} />
                                        ) : (
                                            <FaEye className="password-toggle-icon" onClick={togglePasswordVisibility} />
                                        )}
                                    </div>
                                </div>
                                <div className="register">
                                    <a href="/register">{t('noAccount')}</a>
                                </div>
                                <button type="submit" className="btn-primary" disabled={loading}>
                                    {loading ? t('login.signingIn', 'Signing in...') : t('signIn')}
                                </button>
                            </form>
                            <div className="login-divider">
                                <span>{t('login.socials')}</span>
                            </div>
                            <div className="google-button-wrapper">
                                <GoogleOAuthProvider clientId={googleClientId}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleLoginSuccess}
                                        onError={handleGoogleLoginError}
                                        useOneTap={false}
                                        theme="outline"
                                        text="signin"
                                        shape="rectangular"
                                        size="large"
                                        locale="cs"
                                        disabled={loading}
                                    />
                                </GoogleOAuthProvider>
                            </div>
                        </>
                    )}
                </div>

                {/* Podmínky zobrazíme jen u formuláře */}
                {!inviteSuccess && (
                    <div className="terms-card">
                        <p>
                            {t('login.consent')}<a href="/legal" target="_blank" rel="noopener noreferrer">{t('login.terms')}</a> {t('login.and')} <a href="/legal" target="_blank" rel="noopener noreferrer">{t('login.privacy')}</a>
                        </p>
                    </div>
                )}
            </div>

            {loading && !inviteSuccess && <div className="login-loading">
                <div className="spinner"></div>
                <p>{t('loading')}</p>
            </div>}
        </div>
    );
};

export default LoginForm;